import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {Location} from "@angular/common";
import {AlertService, InvoiceService, StorageService, UserService} from "../_services";
import {Form} from '../_forms';
import {
  DashboardFilter,
  FeeTypeLabels,
  Invoice,
  InvoiceItem,
  InvoiceStatusLabels,
  User,
  TransactionType,
} from "../_models";
import {formatISODate, formatMoney, parseISODateString} from "../_helpers";
import {takeUntil} from "rxjs/operators";
import jsPDF from 'jspdf';
import html2canvas from "html2canvas";
import * as momentTz from "moment-timezone";
import {NgxSmartModalService} from 'ngx-smart-modal';

@Component({
  moduleId: module.id.toString(),
  templateUrl: './invoice-detail.component.html',
  styleUrls: ['./invoice.component.css'],
  selector: 'invoice-detail'
})
export class InvoiceDetailComponent extends Form implements OnInit, OnChanges {
  @Input('invoiceId') invoiceId: string | number = null;
  @Input('pricingOnly') pricingOnly = false
  invoice: Invoice = null;
  user: User;
  downloadingInvoice: boolean = false
  timezone: string = ''
  filters: DashboardFilter = null
  transferFromType: TransactionType = TransactionType.UsageCharge;
  transferToType: TransactionType = TransactionType.Charge;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected invoiceService: InvoiceService,
    protected alertService: AlertService,
    private userService: UserService,
    private modalService: NgxSmartModalService,
    protected storageService: StorageService
  ) {
    super(alertService, router, location);
    this.title = 'Invoice Details';
  }

  ngOnInit() {
    super.ngOnInit();

    this.userService.getCurrent()
      .subscribe(
        (user: User) => {
          this.user = user;
        },
        error => {
          this.handleError(error);
        }
      );

    if (!this.invoiceId) {
      this.route.params.pipe(takeUntil(this.destroy$)).subscribe(
        (params) => {
          this.invoiceId = params['id'];
          this.updateData();
        },
        error => {
          this.handleError(error);
        }
      );
    }

    let timezone = this.storageService.get('timezone');
    if (!timezone) {
      timezone = momentTz.tz.guess();
    }
    this.timezone = timezone;
  }

  ngOnChanges() {
    this.updateData()
  }

  updateData() {
    if (this.invoice && this.invoice.id === this.invoiceId) {
      return;
    }
    this.invoice = null;

    let result = this.pricingOnly ? this.invoiceService.price(this.invoiceId) : this.invoiceService.get(this.invoiceId);

    result.pipe(takeUntil(this.destroy$)).subscribe(
      (invoice: Invoice) => {
        this.invoice = invoice;
        this.filters = {start_date: invoice.start_date,
          end_date: invoice.end_date, timezone: this.timezone}
      },
      error => {
        this.handleError(error);
      }
    );
  }

  getInvoiceDate() {
    const date = parseISODateString(this.invoice.settle_date);

    return date.toLocaleDateString("en-US", {timeZone: 'UTC'});
  }

  getBillingStartDate() {
    const date = parseISODateString(this.invoice.billing_start_date);

    return date.toLocaleDateString("en-US", {timeZone: 'UTC'});
  }

  getDateRange() {
    const startDate = parseISODateString(this.invoice.start_date);
    const endDate = parseISODateString(this.invoice.last_date);

    return startDate.toLocaleDateString("en-US", {timeZone: 'UTC'}) + ' - ' +
      endDate.toLocaleDateString("en-US", {timeZone: 'UTC'});
  }

  getSubscriptionDateRange() {
    const startDate = parseISODateString(this.invoice.subscription_start_date);
    const endDate = parseISODateString(this.invoice.subscription_end_date);

    return startDate.toLocaleDateString("en-US", {timeZone: 'UTC'}) + ' - ' +
      endDate.toLocaleDateString("en-US", {timeZone: 'UTC'});
  }

  getLastSubscriptionDateRange() {
    const startDate = parseISODateString(this.invoice.last_subscription_start_date);
    const endDate = parseISODateString(this.invoice.last_subscription_end_date);

    return startDate.toLocaleDateString("en-US", {timeZone: 'UTC'}) + ' - ' +
      endDate.toLocaleDateString("en-US", {timeZone: 'UTC'});
  }

  getStatus() {
    return InvoiceStatusLabels[this.invoice.status];
  }

  getNumber(item, field) {
    let fields = field.split('.');
    let amount = item;
    fields.forEach(key => {
      if (key in amount && amount[key]) {
        amount = amount[key];
      }
    })
    if (typeof amount === "object") {
      amount = 0;
    }

    return amount;
  }

  getAmount(item, field, prefix: string = '$', negate: boolean = false) {
    const amount = this.getNumber(item, field);
    return prefix + formatMoney(negate ? -Number(amount) : amount);
  }

  hasAmount(item, field) {
    return Math.abs(Number(this.getNumber(item, field))) > 0.000000001
  }

  isPositive(item, field) {
    return Number(this.getNumber(item, field)) > 0.000000001
  }

  isNegative(item, field) {
    return Number(this.getNumber(item, field)) < -0.000000001
  }

  getItemType(item: InvoiceItem) {
    return FeeTypeLabels[item.type];
  }

  formatTimestampAsDate(timestamp: string) {
    return formatISODate(timestamp);
  }

  downloadPDF() {
    let data = document.getElementById('invoiceData');
    this.downloadingInvoice = true;

    html2canvas(data, {
      scrollX: 0,
      scrollY: 0
    }).then(canvas => {
      let margin = 10
      let fileWidth = 208 - 2 * margin;
      let fileHeight = canvas.height * fileWidth / canvas.width;

      const FILEURI = canvas.toDataURL('image/png')
      let PDF = new jsPDF('p', 'mm', 'a4');
      let position = margin;
      PDF.addImage(FILEURI, 'PNG', margin, position, fileWidth, fileHeight)
      let str = "Solvpath LLC   244 Pettit Ave Bellmore, NY 11710   P. 877-805-4520   E. billing@solvpath.com";
      PDF.setFontSize(8);// optional
      PDF.text(str, 45, PDF.internal.pageSize.height - 5);
      PDF.save(`invoice-${this.invoice.id}.pdf`);
      this.downloadingInvoice = false;
    });
  }

  refresh() {
    this.invoice = null;
    this.updateData();
  }

  get canTransferUsageCredit() {
    if (this.user && this.user.is_staff && this.invoice) {
      return Number(this.invoice.usage_amount_due) < 0;
    }

    return false;
  }

  get canTransferSubscriptionCredit() {
    if (this.user && this.user.is_staff && this.invoice) {
      return true;
    }

    return false;
  }

  transferCredit(from: TransactionType, to: TransactionType) {
    this.transferFromType = from;
    this.transferToType = to;
    this.modalService.getModal('invoiceTransferCreditDialog').open();
  }

  onInvoiceCreditTransferred(invoice: Invoice) {
    this.modalService.getModal('invoiceTransferCreditDialog').close();
    this.refresh();
  }

  get canChangeBillingDate() {
    return (this.user && this.user.is_superuser && this.invoice && this.invoice.billing_start_date);
  }

  changeBillingDate() {
    if (this.canChangeBillingDate) {
      this.modalService.getModal('invoiceBillingDateDialog').open();
    }
  }

  onInvoiceBillingDateChanged(invoice: Invoice) {
    this.modalService.getModal('invoiceBillingDateDialog').close();
    this.refresh();
  }

  protected readonly TransactionType = TransactionType;
}
